/* ==========================================================================
   Orska Pro Vault — vanilla JS for vault interactions
   Loaded by every section/vault-*.liquid file with `defer`.
   Each behavior is idempotent and safe to re-init on shopify:section:load.
   ========================================================================== */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     1. Visual Edit filter chips
     Filter masonry tiles by data-category. "all" shows everything.
     ---------------------------------------------------------------------- */
  function initVisualEditFilters(scope) {
    const grids = scope.querySelectorAll('[data-vault-visual-edit]');
    grids.forEach(function (grid) {
      if (grid.dataset.vaultFiltersInit === '1') return;
      grid.dataset.vaultFiltersInit = '1';

      const chips = grid.parentElement
        ? grid.parentElement.querySelectorAll('.chip[data-filter]')
        : grid.querySelectorAll('.chip[data-filter]');

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          const target = chip.getAttribute('data-filter');
          chips.forEach(function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          grid.setAttribute('data-active-filter', target);
          // Reset to the first page so each new filter starts at page 1.
          grid.setAttribute('data-current-page', '1');
          updateVisualEditPagination(grid);
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     2. Visual Edit pagination — numbered pages
     The tiles matching the active filter + search are split into fixed-size
     pages of data-batch-size tiles. Prev/next arrows and numbered page
     buttons swap the visible page instead of appending more tiles.
     ---------------------------------------------------------------------- */
  function initVisualEditPagination(scope) {
    const grids = scope.querySelectorAll('[data-vault-visual-edit]');
    grids.forEach(function (grid) {
      if (grid.dataset.vaultPaginationInit === '1') return;
      grid.dataset.vaultPaginationInit = '1';

      const wrapper = grid.closest('section') || grid.parentElement || grid;
      const prevBtn = wrapper.querySelector('[data-vault-prev]');
      const nextBtn = wrapper.querySelector('[data-vault-next]');
      const pagesEl = wrapper.querySelector('[data-vault-pages]');

      if (!grid.getAttribute('data-current-page')) {
        grid.setAttribute('data-current-page', '1');
      }

      function goToPage(page) {
        grid.setAttribute('data-current-page', String(page));
        updateVisualEditPagination(grid);
        // Bring the top of the grid back into view after a page change so
        // the visitor is not left scrolled halfway down the previous page.
        const top = grid.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          const current = parseInt(grid.getAttribute('data-current-page') || '1', 10);
          if (current > 1) goToPage(current - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          const current = parseInt(grid.getAttribute('data-current-page') || '1', 10);
          goToPage(current + 1);
        });
      }
      if (pagesEl) {
        // Delegate — the numbered buttons are rebuilt on every render.
        pagesEl.addEventListener('click', function (e) {
          const btn = e.target.closest('[data-vault-page]');
          if (!btn) return;
          const page = parseInt(btn.getAttribute('data-vault-page'), 10);
          if (!isNaN(page)) goToPage(page);
        });
      }

      updateVisualEditPagination(grid);
    });
  }

  // Build a windowed list of page numbers so long libraries do not spill a
  // huge row of buttons. Shows first, last, current and its neighbors, with
  // '...' placeholders for the gaps.
  function buildVisualEditPageList(current, count) {
    const pages = [];
    if (count <= 7) {
      for (let i = 1; i <= count; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(count - 1, current + 1);
    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < count - 1) pages.push('...');
    pages.push(count);
    return pages;
  }

  /* ----------------------------------------------------------------------
     2c. Visual Edit search input
     Optional search bar above the chip filter row. Matches against the
     tile's data-title (asset title) and data-asset-code. Combines with
     the chip filter — both must match for a tile to be visible.
     ---------------------------------------------------------------------- */
  function initVisualEditSearch(scope) {
    const grids = scope.querySelectorAll('[data-vault-visual-edit]');
    grids.forEach(function (grid) {
      if (grid.dataset.vaultSearchInit === '1') return;
      grid.dataset.vaultSearchInit = '1';

      const wrapper = grid.closest('section') || grid.parentElement || grid;
      const search = wrapper.querySelector('[data-vault-visual-edit-search]');
      const clearBtn = wrapper.querySelector('[data-vault-visual-edit-search-clear]');
      if (!search) return;

      function applySearch() {
        const query = search.value.toLowerCase().trim();
        grid.setAttribute('data-active-search', query);
        if (clearBtn) clearBtn.toggleAttribute('hidden', query === '');
        // Reset to the first page so search results start at page 1.
        grid.setAttribute('data-current-page', '1');
        updateVisualEditPagination(grid);
      }

      search.addEventListener('input', applySearch);

      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          search.value = '';
          applySearch();
          search.focus();
        });
      }
    });
  }

  function updateVisualEditPagination(grid) {
    // The pagination controls sit as a sibling of the grid in the DOM; look
    // up through the section wrapper so the JS can find them from anywhere.
    const wrapper = grid.closest('section') || grid.parentElement || grid;
    const countEl = wrapper.querySelector('[data-vault-count]');
    const pagesEl = wrapper.querySelector('[data-vault-pages]');
    const prevBtn = wrapper.querySelector('[data-vault-prev]');
    const nextBtn = wrapper.querySelector('[data-vault-next]');
    const paginationRow = wrapper.querySelector('[data-vault-pagination]');
    const allTiles = grid.querySelectorAll('.masonry-tile');
    const pageSize = Math.max(1, parseInt(grid.getAttribute('data-batch-size') || '24', 10));
    const activeFilter = grid.getAttribute('data-active-filter') || 'all';
    const activeSearch = (grid.getAttribute('data-active-search') || '').toLowerCase().trim();

    // First pass — collect the tiles that match the active chip filter AND
    // the active search query. Non-matching tiles are hidden outright.
    const matched = [];
    allTiles.forEach(function (tile) {
      const category = tile.getAttribute('data-category') || '';
      const title = (tile.getAttribute('data-title') || '').toLowerCase();
      const assetCode = (tile.getAttribute('data-asset-code') || '').toLowerCase();
      const matchesFilter = activeFilter === 'all' || category === activeFilter;
      const matchesSearch = activeSearch === '' ||
        title.indexOf(activeSearch) !== -1 ||
        assetCode.indexOf(activeSearch) !== -1 ||
        category.toLowerCase().indexOf(activeSearch) !== -1;

      if (matchesFilter && matchesSearch) {
        matched.push(tile);
      } else {
        tile.setAttribute('hidden', '');
      }
    });

    const total = matched.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // Clamp the requested page into the valid range — filters and search can
    // shrink the matched set below the page the visitor was on.
    let currentPage = parseInt(grid.getAttribute('data-current-page') || '1', 10);
    if (isNaN(currentPage) || currentPage < 1) currentPage = 1;
    if (currentPage > pageCount) currentPage = pageCount;
    grid.setAttribute('data-current-page', String(currentPage));

    // Second pass — reveal only the tiles that fall on the current page.
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    matched.forEach(function (tile, i) {
      tile.toggleAttribute('hidden', i < startIndex || i >= endIndex);
    });

    if (countEl) {
      countEl.textContent = total === 0
        ? 'No images'
        : 'Page ' + currentPage + ' of ' + pageCount;
    }

    // Rebuild the numbered page buttons.
    if (pagesEl) {
      pagesEl.innerHTML = '';
      buildVisualEditPageList(currentPage, pageCount).forEach(function (item) {
        if (item === '...') {
          const gap = document.createElement('span');
          gap.className = 've-page-ellipsis';
          gap.textContent = '…';
          pagesEl.appendChild(gap);
          return;
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 've-page-num' + (item === currentPage ? ' is-active' : '');
        btn.setAttribute('data-vault-page', String(item));
        if (item === currentPage) btn.setAttribute('aria-current', 'page');
        btn.textContent = String(item);
        pagesEl.appendChild(btn);
      });
    }

    if (prevBtn) prevBtn.disabled = currentPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPage >= pageCount;

    // Hide the whole control row when everything fits on a single page.
    if (paginationRow) {
      paginationRow.style.display = pageCount <= 1 ? 'none' : '';
    }
  }

  /* ----------------------------------------------------------------------
     3. Training Library category tabs
     Tabs filter by category. "all" shows every category block.
     ---------------------------------------------------------------------- */
  function initTrainingTabs(scope) {
    const sections = scope.querySelectorAll('[data-vault-training]');
    sections.forEach(function (section) {
      if (section.dataset.vaultTabsInit === '1') return;
      section.dataset.vaultTabsInit = '1';

      const chips = section.querySelectorAll('.chip[data-tab]');
      const blocks = section.querySelectorAll('[data-vault-training-category]');

      function applyFilter(target) {
        blocks.forEach(function (block) {
          const cat = block.getAttribute('data-vault-training-category');
          const visible = target === 'all' || cat === target;
          block.toggleAttribute('hidden', !visible);
          // Also clear the inline display style that Liquid renders for
          // server-side hiding. Without this, the inline !important
          // display:none would override any CSS attempting to show it.
          block.style.cssText = visible ? '' : 'display: none !important;';
        });
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          const target = chip.getAttribute('data-tab');
          chips.forEach(function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          applyFilter(target);
        });
      });

      // Apply the initial filter on page load. Read from the section's
      // data-initial-filter attribute (server-rendered in Liquid) which
      // is the authoritative source of truth. Fall back to the active
      // chip's data-tab if that attribute is missing, then to 'all'.
      let initialFilter = section.getAttribute('data-initial-filter');
      if (!initialFilter || initialFilter === '') {
        const activeChip = section.querySelector('.chip.is-active[data-tab]');
        initialFilter = activeChip ? activeChip.getAttribute('data-tab') : 'all';
      }

      // Sync chip is-active state to match the resolved initial filter.
      chips.forEach(function (c) {
        const target = c.getAttribute('data-tab');
        c.classList.toggle('is-active', target === initialFilter);
      });

      applyFilter(initialFilter);
    });
  }

  /* ----------------------------------------------------------------------
     2b. Playlists pagination
     Hides playlist cards past data-batch-size; clicks on the View More
     button reveal the next batch. Each section operates independently.
     ---------------------------------------------------------------------- */
  function initPlaylistsPagination(scope) {
    const grids = scope.querySelectorAll('[data-vault-playlists]');
    grids.forEach(function (grid) {
      if (grid.dataset.vaultPlaylistsInit === '1') return;
      grid.dataset.vaultPlaylistsInit = '1';

      const wrapper = grid.closest('section') || grid.parentElement || grid;
      const button = wrapper.querySelector('[data-vault-playlists-more]');
      const lessButton = wrapper.querySelector('[data-vault-playlists-less]');
      const countEl = wrapper.querySelector('[data-vault-playlists-count]');
      const cards = grid.querySelectorAll('.playlist-card');
      const batchSize = parseInt(grid.getAttribute('data-batch-size') || '3', 10);

      let visibleBatches = parseInt(grid.getAttribute('data-visible-batches') || '1', 10);

      function update() {
        const total = cards.length;
        const visibleCap = visibleBatches * batchSize;
        let shown = 0;
        cards.forEach(function (card, i) {
          const within = i < visibleCap;
          card.toggleAttribute('hidden', !within);
          if (within) shown += 1;
        });
        if (countEl) countEl.textContent = 'Showing ' + shown + ' of ' + total;
        if (button) {
          const atEnd = visibleCap >= total;
          button.disabled = atEnd;
          button.textContent = atEnd
            ? (button.getAttribute('data-end-label') || 'All shown')
            : (button.getAttribute('data-default-label') || 'View More Playlists');
        }
        if (lessButton) {
          lessButton.toggleAttribute('hidden', visibleBatches <= 1);
        }
      }

      if (button) {
        button.addEventListener('click', function () {
          visibleBatches += 1;
          update();
        });
      }
      if (lessButton) {
        lessButton.addEventListener('click', function () {
          visibleBatches = 1;
          update();
        });
      }

      update();
    });
  }

  /* ----------------------------------------------------------------------
     3a. Training Library search
     Optional search bar above or below the chip filters. Matches against
     each card's data-title, data-desc, and data-category. Combines with
     the chip filter — both must match for a card to be visible.
     ---------------------------------------------------------------------- */
  function initTrainingSearch(scope) {
    const sections = scope.querySelectorAll('[data-vault-training]');
    sections.forEach(function (section) {
      if (section.dataset.vaultTrainingSearchInit === '1') return;
      section.dataset.vaultTrainingSearchInit = '1';

      const search = section.querySelector('[data-vault-training-search]');
      const clearBtn = section.querySelector('[data-vault-training-search-clear]');
      if (!search) return;

      function applySearch() {
        const query = search.value.toLowerCase().trim();
        section.setAttribute('data-active-search', query);
        if (clearBtn) clearBtn.toggleAttribute('hidden', query === '');
        // Re-run each category group's pagination update so the search
        // filter is respected. Also reset visible count per category so
        // the matching cards show from the top.
        const groups = section.querySelectorAll('.training-category-group[data-initial-count]');
        groups.forEach(function (group) {
          // Manually re-trigger update — but we need to talk to the
          // closure in initTrainingPagination. Easiest path: fire a
          // synthetic event that the pagination init listens for.
          group.dispatchEvent(new CustomEvent('vault:training:search-changed', { bubbles: false }));
        });
      }

      search.addEventListener('input', applySearch);

      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          search.value = '';
          applySearch();
          search.focus();
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     3b. Training Library per-category pagination
     Each .training-category-group manages its own visible-count state.
     Cards beyond data-initial-count are hidden on init; clicking the
     [data-training-more] button reveals the next batch (size = initial).
     Each group operates independently — clicking View More in Welcome
     doesn't affect any other category's pagination state.
     ---------------------------------------------------------------------- */
  function initTrainingPagination(scope) {
    const groups = scope.querySelectorAll('.training-category-group[data-initial-count]');
    groups.forEach(function (group) {
      if (group.dataset.trainingPaginationInit === '1') return;
      group.dataset.trainingPaginationInit = '1';

      const grid = group.querySelector('.training-grid');
      if (!grid) return;
      const cards = grid.querySelectorAll('.training-card');
      const button = group.querySelector('[data-training-more]');
      const lessButton = group.querySelector('[data-training-less]');
      const countEl = group.querySelector('[data-training-count]');
      const initialCount = parseInt(group.dataset.initialCount || '4', 10);
      const section = group.closest('[data-vault-training]');

      let visible = initialCount;

      function update() {
        const activeSearch = section ? (section.getAttribute('data-active-search') || '').toLowerCase().trim() : '';

        // Filter cards by search query first. Cards that don't match
        // are hidden regardless of pagination state. Cards that match
        // are then paginated by the visible count.
        let matchIndex = 0;
        cards.forEach(function (card) {
          const title = (card.getAttribute('data-title') || '').toLowerCase();
          const desc = (card.getAttribute('data-desc') || '').toLowerCase();
          const category = (card.getAttribute('data-category') || '').toLowerCase();
          const matchesSearch = activeSearch === '' ||
            title.indexOf(activeSearch) !== -1 ||
            desc.indexOf(activeSearch) !== -1 ||
            category.indexOf(activeSearch) !== -1;

          if (!matchesSearch) {
            card.toggleAttribute('hidden', true);
            card.classList.add('is-paginated-hidden');
            card.style.display = 'none';
            return;
          }

          // Within the search-matched subset, apply pagination by index.
          const shouldHide = matchIndex >= visible;
          card.toggleAttribute('hidden', shouldHide);
          card.classList.toggle('is-paginated-hidden', shouldHide);
          card.style.display = shouldHide ? 'none' : '';
          matchIndex += 1;
        });

        const total = matchIndex;
        const shown = Math.min(visible, total);
        if (countEl) {
          countEl.textContent = 'Showing ' + shown + ' of ' + total;
        }
        if (button) {
          const atEnd = visible >= total;
          button.disabled = atEnd || total === 0;
          if (atEnd) {
            button.textContent = button.getAttribute('data-end-label') || 'All shown';
          } else {
            button.textContent = button.getAttribute('data-default-label') || 'View More';
          }
        }
        if (lessButton) {
          lessButton.toggleAttribute('hidden', visible <= initialCount);
        }
        // Hide the whole category group when there are zero search matches.
        group.toggleAttribute('hidden', activeSearch !== '' && total === 0);
      }

      if (button) {
        button.addEventListener('click', function () {
          visible += initialCount;
          update();
        });
      }
      if (lessButton) {
        lessButton.addEventListener('click', function () {
          visible = initialCount;
          update();
        });
      }

      // Listen for search input changes from initTrainingSearch and re-run.
      group.addEventListener('vault:training:search-changed', function () {
        visible = initialCount;
        update();
      });

      update();
    });
  }

  /* ----------------------------------------------------------------------
     4. Sticky section nav scroll-spy
     Highlights the active anchor based on the section currently in view.
     ---------------------------------------------------------------------- */
  function initStickyNav(scope) {
    const navs = scope.querySelectorAll('[data-vault-section-nav]');
    navs.forEach(function (nav) {
      if (nav.dataset.vaultNavInit === '1') return;
      nav.dataset.vaultNavInit = '1';

      const links = nav.querySelectorAll('a[href^="#"]');
      const targets = [];
      links.forEach(function (link) {
        const id = link.getAttribute('href').slice(1);
        const el = document.getElementById(id);
        if (el) targets.push({ id: id, el: el, link: link });
      });
      if (!targets.length) return;

      // Smooth scroll with sticky-nav offset.
      const navHeight = nav.getBoundingClientRect().height || 60;
      links.forEach(function (link) {
        link.addEventListener('click', function (e) {
          const id = link.getAttribute('href').slice(1);
          const target = document.getElementById(id);
          if (!target) return;
          e.preventDefault();
          const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
      });

      // Honor the data-disable-scroll-spy attribute set by the section's
      // "Enable scroll-spy" toggle. When disabled, smooth scroll still
      // works on click but no link auto-activates based on scroll position.
      if (nav.getAttribute('data-disable-scroll-spy') === 'true') return;

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const activeId = entry.target.id;
          targets.forEach(function (t) {
            t.link.classList.toggle('is-active', t.id === activeId);
          });
        });
      }, { rootMargin: '-40% 0px -50% 0px', threshold: 0 });

      targets.forEach(function (t) { observer.observe(t.el); });
    });
  }

  /* ----------------------------------------------------------------------
     5. Contributor Circle form (light client-side enhancements only)
     Shopify Forms handles submission/validation server-side. We just:
       - mark the file input area when a file is selected
       - keep the interests[] checkbox group in tact (no transformation)
     ---------------------------------------------------------------------- */
  function initContributorForm(scope) {
    const forms = scope.querySelectorAll('form.contributor-form');
    forms.forEach(function (form) {
      if (form.dataset.vaultFormInit === '1') return;
      form.dataset.vaultFormInit = '1';

      const fileInput = form.querySelector('input[type=file]');
      const fileMsg = form.querySelector('[data-vault-file-msg]');
      if (fileInput && fileMsg) {
        const defaultMsg = fileMsg.textContent;
        fileInput.addEventListener('change', function () {
          if (fileInput.files && fileInput.files.length) {
            fileMsg.textContent = fileInput.files.length + ' file(s) selected';
          } else {
            fileMsg.textContent = defaultMsg;
          }
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     Init dispatcher
     ---------------------------------------------------------------------- */
  function init(scope) {
    scope = scope || document;
    initVisualEditFilters(scope);
    initVisualEditPagination(scope);
    initVisualEditSearch(scope);
    initPlaylistsPagination(scope);
    initTrainingTabs(scope);
    initTrainingSearch(scope);
    initTrainingPagination(scope);
    initStickyNav(scope);
    initContributorForm(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();